import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { BaseDialog } from '../../components/dialogs/basedialog/base.dialog';
import { MessageBoxHelper } from '../../components/dialogs/messagebox/messagebox.dialog';
import { TranslateFormatText } from '../../helpers/array.helpers';
import { LayoutHelper } from '../../helpers/layout.helper';
import { AttributeProperty, ContainerClassObject } from '../../models/ContainerClassObject';
import { MessageBoxButtons } from '../../models/enums/messageboxbuttons.enum';
import { MessageBoxIcon } from '../../models/enums/messageboxicon.enum';
import { MessageBoxResult } from '../../models/enums/messageboxresult.enum';
import { Layout } from '../../models/layout.model';
import { Color } from '../../models/style/color.model';
import { Font } from '../../models/style/font.model';
import { TranslatedString } from '../../models/translatedstring.model';
import { DataService } from '../../services/data.service';
import { ContainerEnumService, DataModelService, DataSourceQuery } from '../../services/datamodel.service';
import { LayoutService } from '../../services/layout.service';
import { MetaService } from '../../services/meta.service';
import { SideNavService } from '../../services/sidenav.service';
import { DataObjectFieldDialog } from '../../settings/datamodel/dataobject/dataobject.field.dialog';

@Component({
    selector: 'app-wizard',
    templateUrl: './app-wizard.component.html',
    styleUrls: ['./app-wizard.component.css']
})
export class AppWizardComponent implements OnInit, OnDestroy {
    wizardForm: FormGroup;
    step = 1;
    containerFields = [];
    allTypes = [];
    dataSources = [];
    selectedDataSource: string;
    allTables = [];
    allStaticTables = [];
    allEnums = [];
    rows = [];
    fieldChanged = false;
    hasUnsavedChanges = false;
    clientURL = location.origin;
    existingApps: string[];
    existingPages: string[] = [];
    pageCount = 4;
    private formChangeSubscription: Subscription;

    constructor(
        private fb: FormBuilder,
        private dataService: DataService,
        private dataModelService: DataModelService,
        private enumService: ContainerEnumService,
        private metaService: MetaService,
        private router: Router
    ) { }

    ngOnInit() {
        this.wizardForm = this.fb.group({
            appName: ['', Validators.required],
            logo: [null],
            generateTheme: [false],
            themeName: [''],
            primaryColor: ['#000000'],
            font: ['Arial'],
            fontSize: ['16'],
            navType: ['horizontal'],
            loginStyle: ['classic'],
            pageName: [''],
            pageUrl: ['', Validators.required],
            isLandingPage: [false],
            generateObject: [false],
            objectName: [''],
            dataSource: [''],
        });

        this.metaService.LoadNavigationStructures().subscribe(x => {
            const list = [];
            if (x) {
                x.forEach((y: any) => {
                    list.push(y.Caption.toLowerCase());
                });
            }
            this.existingApps = list;
        });
        this.metaService.LoadAllPages().subscribe(x => {
            const list = [];
            if (x) {
                x.forEach((y: any) => {
                    list.push(y.URL.toLowerCase());
                });
            }
            this.existingPages = list;
        });

        this.loadLists();

        this.formChangeSubscription = this.wizardForm.valueChanges.subscribe((x: any) => {
            this.hasUnsavedChanges = true;
            LayoutService.PreventBrowserClose.next(true);
            if (x) {
                if (x.dataSource != this.selectedDataSource) {
                    this.selectedDataSource = x.dataSource;
                    this.loadTablesAndEnums();
                }
            }
        });

    }

    ngOnDestroy() {
        if (this.formChangeSubscription) {
            this.formChangeSubscription.unsubscribe();
        }
        LayoutService.PreventBrowserClose.next(false);
    }

    onBeforeUnload = (e: BeforeUnloadEvent) => {
        if (this.hasUnsavedChanges) {
            e.preventDefault();
            e.returnValue = '';
        }
    }

    loadLists() {
        const query = new DataSourceQuery(null);
        query.DBUsages = [[16, 1048576], [32, 2097152]];
        query.ResourceBase = 0;
        this.dataModelService.GetDataSourcesByQuery(query).subscribe(sources => {
            this.dataSources = sources;
        });
        this.dataService.GetList('dynamic', 'GetAllStaticTables').subscribe((tables) => {
            if (tables) {
                this.allStaticTables = tables;
            }
        });
        this.dataService.GetList('dynamic', 'GetDynamicTypes').subscribe((data) => {
            if (data) {
                this.allTypes = data;
            }
            this.allTypes.push({ Caption: 'Static', Value: '9DFCE24E-FCB8-4CD8-B724-EFB87810EE80' });
        });
    }

    loadTablesAndEnums() {
        this.allTables = [];
        this.allEnums = [];
        if (this.selectedDataSource) {
            const ds = this.dataSources.find(x => x.SID == this.selectedDataSource);
            if (ds) {
                const path = 'GetTablesByDataSource?dataModel' + ds.DataModelID + '&dataSource=' + ds.SID;
                this.dataService.GetList('dynamic', path).subscribe((data) => {
                    if (data) {
                        this.allTables = data.map((table: any) => ({
                            Caption: table.Caption,
                            ID: table.SID,
                            IsCapsule: table.IsCapsule,
                            IsOverridden: table.IsOverridden
                        }));
                    }
                });
                this.enumService.GetEnumsByDataModel(ds.DataModelID).subscribe(enums => {
                    if (enums) {
                        this.allEnums = enums.map((en: any) => ({
                            Caption: en.Caption,
                            Value: en.SID
                        }));
                    }
                });
            }
        }
    }

    updatePageUrl() {
        const pageName = this.wizardForm.get('pageName').value;
        const pageUrl = pageName.toLowerCase().replace(/\s+/g, '-');
        this.wizardForm.patchValue({ pageUrl });
    }

    async handleNext() {
        if (this.step < this.pageCount) {
            if (this.step === 1) {
                const appName = this.wizardForm.get('appName').value;
                if (appName) {
                    const toLower = appName.toLowerCase();
                    if (SideNavService.IsSystemURL('/' + toLower)) {
                        MessageBoxHelper.ShowDialog(
                            new TranslateFormatText('@@GA-AW-NameIsSystemURLText'),
                            new TranslateFormatText('@@GA-AW-NameIsSystemURLHeader'),
                            MessageBoxButtons.Ok,
                            MessageBoxIcon.Information
                        );
                        return;
                    } else if (this.existingApps) {
                         if (this.existingApps.some(app => app == toLower)) {
                            MessageBoxHelper.ShowDialog(
                                new TranslateFormatText('@@GA-AW-NameAlreadyExistingText'),
                                new TranslateFormatText('@@GA-AW-NameAlreadyExistingHeader'),
                                MessageBoxButtons.Ok,
                                MessageBoxIcon.Information
                            );
                            return;
                        }
                    } else {
                        return;
                    }
                } else {
                    MessageBoxHelper.ShowDialog(
                        new TranslateFormatText('@@GA-AW-SetNameText'),
                        new TranslateFormatText('@@GA-AW-SetNameHeader'),
                        MessageBoxButtons.Ok,
                        MessageBoxIcon.Information
                    );
                    return;
                }
            } else if (this.step === 2) {
                if (this.wizardForm.get('generateTheme').value && !this.wizardForm.get('themeName').value) {
                    MessageBoxHelper.ShowDialog(
                        new TranslateFormatText('@@GA-AW-SetThemeNameText'),
                        new TranslateFormatText('@@GA-AW-SetThemeNameHeader'),
                        MessageBoxButtons.Ok,
                        MessageBoxIcon.Information
                    );
                    return;
                }
            } else if (this.step === 3) {
                let url = this.wizardForm.get('pageUrl').value;
                if (url) {
                    if (!url.startsWith('/')) {
                        url = '/' + url;
                    }
                    const toLower = url.toLowerCase();
                    if (this.existingPages.some(x => x == toLower)) {
                        MessageBoxHelper.ShowDialog(
                            new TranslateFormatText('@@GA-AW-URLAlreadyExistingText'),
                            new TranslateFormatText('@@GA-AW-URLAlreadyExistingHeader'),
                            MessageBoxButtons.Ok,
                            MessageBoxIcon.Information
                        );
                        return;
                    }
                } else {
                    MessageBoxHelper.ShowDialog(
                        new TranslateFormatText('@@GA-AW-SetURLText'),
                        new TranslateFormatText('@@GA-AW-SetURLHeader'),
                        MessageBoxButtons.Ok,
                        MessageBoxIcon.Information
                    );
                    return;
                }
            }
            this.step++;
        }
    }

    handlePrev() {
        if (this.step > 1) this.step--;
    }

    async saveAppName() {
        const appName = this.wizardForm.get('appName').value;
        // Here you would typically make an API call to save the app name
        console.log('Saving app name:', appName);
        // Simulating an API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        this.hasUnsavedChanges = false;
    }

    handleLogoUpload(event: any) {
        const file = event.target.files[0];
        this.wizardForm.patchValue({ logo: file });
    }

    handleAddProperty() {
        this.showDialog(null, (r: any) => {
            this.containerFields.push(r);
            const row = this.fillRow({}, r);
            this.rows.push(row);
            this.fieldChanged = true;
            this.hasUnsavedChanges = true;
            LayoutService.PreventBrowserClose.next(true);
        });
    }

    showDialog(field: any, handler: any) {
        const args = {
            Field: field,
            AllTables: this.allTables,
            AllStaticTables: this.allStaticTables,
            AllEnums: this.allEnums,
            AllTypes: this.allTypes
        };
        BaseDialog.ShowDialog({
            ContentType: DataObjectFieldDialog,
            Handler: (r: any) => {
                if (r && handler) {
                    handler(r);
                }
                return true;
            },
            Title: '@@Feld definieren',
            InitArgs: args
        });
    }

    fillRow(row: any, f: AttributeProperty) {
        row.ID = f.ID;
        row.Name = f.Name;
        row.Caption = TranslatedString.GetTranslation(f.Caption);
        const type = this.allTypes.find(x => x.Value === f.Type);
        if (type) {
            row.Type = type.Caption;
        } else {
            row.Type = f.Type;
        }
        row.ReadOnly = f.IsReadOnly ? 'done' : '';
        row.Mandatory = f.IsMandatory ? 'done' : '';
        row.IsNullable = f.IsNullable ? 'done' : '';
        row.List = f.IsList ? 'done' : '';
        if (f.Type === 'c4ef25dd-c176-418c-836e-f26c52d7f59c' || f.Type === '9DFCE24E-FCB8-4CD8-B724-EFB87810EE80') {
            const table = this.allStaticTables.find(x => x.Value === f.AdvancedType);
            if (table) {
                row.Table = table.Caption;
            } else {
                const table = this.allTables.find(x => x.ID === f.AdvancedType);
                if (table) {
                    row.Table = table.Caption;
                } else {
                    row.Table = f.AdvancedType;
                }
            }
        } else if (f.Type === '876cf188-243b-49ed-91ab-b2cf27216a30') {
            const table = this.allEnums.find(x => x.Value === f.AdvancedType);
            if (table) {
                row.Table = table.Caption;
            } else {
                row.Table = f.AdvancedType;
            }
        } else {
            row.Table = '';
        }
        row.Shared = f.IsShared ? 'done' : '';
        row.Reverse = f.IsReverse ? 'done' : '';
        row.Default = f.Default;
        return row;
    }

    editField(row: any) {
        let fieldIndex: number;
        let field: any;
        if (this.containerFields.some((x, i) => {
            if (x.ID === row.ID) {
                fieldIndex = i;
                field = x;
                return true;
            }
            return false;
        })) {
            this.showDialog(field, (r: any) => {
                this.fillRow(row, r);
                this.fieldChanged = true;
                this.hasUnsavedChanges = true;
                LayoutService.PreventBrowserClose.next(true);
                this.containerFields.splice(fieldIndex, 1, r);
            });
        }
    }

    deleteField(row: any, index: number) {
        const text = new TranslateFormatText('@@Sind Sie sicher, dass Sie das Feld {0} loeschen moechten?');
        text.FormatParams.push(row.Caption);
        MessageBoxHelper.ShowDialog(text, new TranslateFormatText('@@Feld loeschen'),
            MessageBoxButtons.YesNo, MessageBoxIcon.Question).then(x => {
                if (x === MessageBoxResult.Yes) {
                    let fieldIndex = -1;
                    if (this.containerFields.some((f, i) => {
                        if (f.ID === row.ID) {
                            fieldIndex = i;
                            return true;
                        }
                        return false;
                    })) {
                        this.containerFields.splice(fieldIndex, 1);
                        this.rows.splice(index, 1);
                        this.fieldChanged = true;
                        this.hasUnsavedChanges = true;
                        LayoutService.PreventBrowserClose.next(true);
                    }
                }
            });
    }

    async handleExit() {
        if (this.hasUnsavedChanges) {
            const result = await MessageBoxHelper.ShowDialog(
                new TranslateFormatText('Moechten Sie Ihre Aenderungen speichern, bevor Sie den Wizard verlassen?'),
                new TranslateFormatText('Ungespeicherte Aenderungen'),
                MessageBoxButtons.YesNoAbort,
                MessageBoxIcon.Question
            );

            if (result === MessageBoxResult.Yes) {
                await this.saveAppName();
                await this.createNewNavigation();
                this.navigateAway();
            } else if (result === MessageBoxResult.No) {
                this.navigateAway();
            }
            // If No, do nothing and stay in the wizard
        } else {
            this.navigateAway();
        }
    }

    private navigateAway() {
        // Navigate to the dashboard when exiting the wizard
        this.router.navigate(['/dashboard']);
    }

    private async createNewNavigation() {
        const appName = this.wizardForm.get('appName').value;
        const newNavItem = {
            Caption: new TranslatedString(appName),
            Name: appName.toLowerCase().replace(/\s+/g, '-'),
            Icon: 'fa-solid fa-home',
            Url: `/${appName.toLowerCase().replace(/\s+/g, '-')}`,
            ParentID: null,
            SortOrder: 0,
            IsVisible: true
        };

        try {
            const result = await firstValueFrom(this.dataService.Save('SaveNavigationItem', newNavItem));
            if (result && result.Success) {
                console.log('New navigation created:', result.Data);
                // You might want to update the application's navigation state here
                // For example, refreshing the navigation menu
                // this.navigationService.refreshNavigation();
            } else {
                console.error('Failed to create navigation:', result.ErrorMessage);
                // Handle the error, maybe show a message to the user
            }
        } catch (error) {
            console.error('Error creating navigation:', error);
            // Handle the error, maybe show a message to user
        }
    }

    onSubmit() {
        let doIt = false;
        if (this.wizardForm.valid && doIt) {
            const formData = this.wizardForm.value;
            let dataSource: any;
            if (formData.generateObject) {
                let canGenerate = true;
                if (!formData.objectName || !formData.dataSource) {
                    canGenerate = false;
                } else {
                    dataSource = this.dataSources.find(x => x.SID == formData.dataSource);
                    if (!dataSource) {
                        canGenerate = false;
                    }
                }
                if (!canGenerate) {
                    MessageBoxHelper.ShowDialog(
                        new TranslateFormatText('@@GA-AW-SetObjectPropsText'),
                        new TranslateFormatText('@@GA-AW-SetObjectPropsHeader'),
                        MessageBoxButtons.Ok,
                        MessageBoxIcon.Information
                    );
                    return;
                }
            }

            if (formData.appName && formData.pageUrl) {
                LayoutService.Loading.next(true);
                const checkItem = { URL: formData.pageUrl };
                LayoutHelper.checkURL(checkItem);
                const layout = new Layout();
                layout['ElementType'] = 'grid';
                layout['Name'] = 'Base';
                if (formData.pageName) {
                    layout['PageName'] = formData.pageName;
                } else {
                    layout['PageName'] = 'Landing Page';
                }
                // TODO: Content of empty landing page
                // Save Page
                this.metaService.AddOrUpdateLayoutWithKey(layout, checkItem.URL).subscribe((appResult) => {
                    if (appResult) {
                        const app = {
                            _key: formData.appName,
                            LandingPage: checkItem.URL,
                            DefaultTheme: null
                        };
                        new Promise<void>(resolve => {
                            if (formData.generateTheme) {
                                // Save Theme
                                const font = new Font();
                                font.FontFamilyName = formData.font;
                                font.FontSize = formData.fontSize;
                                const theme = {
                                    Name: formData.themeName,
                                    Caption: new TranslatedString(formData.themeName),
                                    Primary: Color.FromHex(formData.primaryColor),
                                    Font: font
                                };
                                this.metaService.SaveTheme(theme).subscribe((themeResult) => {
                                    if (themeResult) {
                                        app.DefaultTheme = themeResult.SID;
                                    }
                                    resolve();
                                });

                            } else {
                                resolve();
                            }
                        }).then(() => {
                            // Save Navigation Structure
                            this.metaService.SaveNavigationStructureToChangeLog(app).subscribe((pageResult) => {
                                if (pageResult) {
                                    // Save Navigation Node
                                    const nodeToSave = {
                                        StructureKey: formData.appName,
                                        Caption: new TranslatedString(layout['PageName']),
                                        Url: checkItem.URL
                                    };
                                    this.metaService.SaveNavigationNode({ NodeToSave: nodeToSave }).subscribe((nodeResult) => {
                                        if (nodeResult) {
                                            if (dataSource) {
                                                const cco = new ContainerClassObject();
                                                cco.Name = formData.objectName;
                                                cco['Caption'] = new TranslatedString(formData.objectName);
                                                cco.DataSourceId = dataSource.SID;
                                                cco.DataModelID = dataSource.DataModelID;
                                                cco.Fields = this.containerFields;
                                                this.dataService.Save('dynamic', cco).subscribe(() => {
                                                    this.hasUnsavedChanges = false;
                                                    LayoutService.PreventBrowserClose.next(false);
                                                    this.router.navigate(['/' + formData.appName]);
                                                });
                                            } else {
                                                this.hasUnsavedChanges = false;
                                                LayoutService.PreventBrowserClose.next(false);
                                                this.router.navigate(['/' + formData.appName]);
                                            }
                                        }
                                    });
                                }
                            });
                        });                                             
                    }
                });                
            }
        }
    }
}
